import React, { useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Form, Input, Popconfirm, Select, Spin, Switch } from 'antd';
import { PlusOutlined, ReloadOutlined, RightOutlined, FileTextOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { MobileFilterBar, SectionPills, FilterGroup } from '../../components/mobile';
import { TOUCH_TARGET } from '../../constants/mobile';
import { ExecutionResponseDto } from '../../types';
import type { CreateDailyScheduleDto, DailyScheduleResponseDto, UpdateDailyScheduleDto } from '../../api-schema/generated/types';
import { describeCron, executionStatusMeta } from './executionStatus';

type DailySchedule = DailyScheduleResponseDto & { portfolioId?: string };

interface Option {
  value: string;
  label: string;
}

export interface MobileExecutionsProps {
  executions: ExecutionResponseDto[];
  loading: boolean;
  onRefresh: () => void;
  onOpenExecution: (execution: ExecutionResponseDto) => void;
  strategyName: (strategyId: string) => string;
  /** Name for an execution's portfolio id when the row came without one. */
  portfolioLabel: (portfolioId: string) => string | null;
  search: string;
  onSearch: (value: string) => void;
  filterGroups: FilterGroup[];

  schedules: DailySchedule[];
  loadingSchedules: boolean;
  scheduleLabel: (schedule: DailySchedule) => string;
  portfolioOptions: Option[];
  strategyOptions: Option[];
  /** Each resolves true when the server accepted it, so the sheet knows whether to close. */
  onCreateSchedule: (values: CreateDailyScheduleDto) => Promise<boolean>;
  onUpdateSchedule: (id: string, values: UpdateDailyScheduleDto) => Promise<boolean>;
  onDeleteSchedule: (id: string) => Promise<boolean>;
}

const PAGE = 20;
const MUTED = '#848E9C';
const CARD = '#1E2329';
const BORDER = '#2B3139';

/** Execution rows carry the trading day as UTC midnight of the Vietnam date. */
const tradingDay = (iso: string) => iso.slice(0, 10);
const todayVN = () => new Date(Date.now() + 7 * 3600_000).toISOString().slice(0, 10);

const dayHeading = (day: string): string => {
  const today = todayVN();
  const yesterday = new Date(Date.parse(today) - 86400_000).toISOString().slice(0, 10);
  if (day === today) return 'Today';
  if (day === yesterday) return 'Yesterday';
  // numberFormat-exempt: date
  return new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
  });
};

// numberFormat-exempt: date
const timeOf = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const meta = executionStatusMeta(status);
  return (
    <span
      style={{
        flexShrink: 0,
        fontSize: 11.5,
        fontWeight: 600,
        color: meta.color,
        background: `${meta.color}1F`,
        borderRadius: 10,
        padding: '3px 9px',
        whiteSpace: 'nowrap',
      }}
    >
      {meta.label}
    </span>
  );
};

const Empty: React.FC<{ icon: React.ReactNode; text: string }> = ({ icon, text }) => (
  <div style={{ textAlign: 'center', color: MUTED, padding: '48px 16px', fontSize: 13 }}>
    <div style={{ fontSize: 36, opacity: 0.5, marginBottom: 12 }}>{icon}</div>
    {text}
  </div>
);

const RunsList: React.FC<Pick<MobileExecutionsProps, 'executions' | 'loading' | 'onOpenExecution' | 'strategyName' | 'portfolioLabel'>> = ({
  executions,
  loading,
  onOpenExecution,
  strategyName,
  portfolioLabel,
}) => {
  const [shown, setShown] = useState(PAGE);
  useEffect(() => setShown(PAGE), [executions]);

  const groups = useMemo(() => {
    const out: { day: string; rows: ExecutionResponseDto[] }[] = [];
    for (const e of executions.slice(0, shown)) {
      const day = tradingDay(e.executionDate);
      const last = out[out.length - 1];
      if (last?.day === day) last.rows.push(e);
      else out.push({ day, rows: [e] });
    }
    return out;
  }, [executions, shown]);

  if (loading) return <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>;
  if (executions.length === 0) return <Empty icon={<FileTextOutlined />} text="No executions found" />;

  return (
    <div>
      {groups.map(({ day, rows }) => (
        <section key={day} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4, margin: '0 4px 6px' }}>
            {dayHeading(day)}
          </div>
          <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: 'hidden' }}>
            {rows.map((e, i) => {
              const meta = executionStatusMeta(e.status);
              return (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => onOpenExecution(e)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    minHeight: 64,
                    padding: '10px 12px 10px 14px',
                    background: 'transparent',
                    border: 'none',
                    borderTop: i ? `1px solid ${BORDER}` : 'none',
                    // A live bot is what the user came to find; mark it at the edge.
                    boxShadow: meta.active ? `inset 3px 0 0 ${meta.color}` : undefined,
                    textAlign: 'left',
                    color: 'inherit',
                    cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: '#EAECEF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {strategyName(e.strategyId)}
                      </span>
                      <StatusPill status={e.status} />
                    </div>
                    <div style={{ fontSize: 12.5, color: '#B7BDC6', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {e.portfolioName || portfolioLabel(e.portfolioId) || e.portfolioId}
                    </div>
                    <div style={{ fontSize: 11, color: '#5E6673', marginTop: 2, fontFamily: 'monospace' }}>
                      #{e.id.slice(0, 8)} · {timeOf(e.createdAt)}
                    </div>
                  </div>
                  <RightOutlined style={{ color: '#5E6673', fontSize: 12 }} />
                </button>
              );
            })}
          </div>
        </section>
      ))}
      {shown < executions.length && (
        <Button block onClick={() => setShown((n) => n + PAGE)} style={{ height: TOUCH_TARGET, marginBottom: 8 }}>
          Show more ({executions.length - shown} left)
        </Button>
      )}
      <div style={{ textAlign: 'center', color: '#5E6673', fontSize: 12, padding: '4px 0 8px' }}>
        {executions.length} execution{executions.length === 1 ? '' : 's'}
      </div>
    </div>
  );
};

type SheetState = { mode: 'add' } | { mode: 'edit'; schedule: DailySchedule } | null;

const ScheduleSheet: React.FC<{
  state: SheetState;
  onClose: () => void;
  props: MobileExecutionsProps;
}> = ({ state, onClose, props }) => {
  const [form] = Form.useForm<{ portfolioId: string; strategyId: string; cronDaily: string; isActive: boolean }>();
  const [saving, setSaving] = useState(false);
  const cron = Form.useWatch('cronDaily', form) ?? '';
  const editing = state?.mode === 'edit' ? state.schedule : null;

  useEffect(() => {
    if (!state) return;
    form.resetFields();
    if (editing) form.setFieldsValue({ cronDaily: editing.cronDaily, isActive: editing.isActive });
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (v: { portfolioId: string; strategyId: string; cronDaily: string; isActive: boolean }) => {
    setSaving(true);
    try {
      const ok = editing
        ? await props.onUpdateSchedule(editing.id, { cronDaily: v.cronDaily.trim(), isActive: v.isActive })
        : await props.onCreateSchedule({ portfolioId: v.portfolioId, strategyId: v.strategyId, cronDaily: v.cronDaily.trim(), isActive: v.isActive !== false } as CreateDailyScheduleDto);
      if (ok) onClose();
    } finally {
      setSaving(false);
    }
  };

  const human = describeCron(cron);
  const field = { height: TOUCH_TARGET };

  return (
    <Drawer
      placement="bottom"
      open={state !== null}
      onClose={onClose}
      height="auto"
      title={editing ? 'Edit daily run' : 'New daily run'}
      // Above the floating support button (PANEL_Z + 1), which otherwise covers Save.
      zIndex={1010}
      styles={{
        header: { background: '#1A1D23', borderBottom: `1px solid ${BORDER}` },
        body: { padding: '16px 16px calc(16px + env(safe-area-inset-bottom))', background: '#1A1D23' },
        content: { borderRadius: '12px 12px 0 0', background: '#1A1D23' },
      }}
    >
      <Form form={form} layout="vertical" requiredMark={false} onFinish={submit} initialValues={{ cronDaily: '0 9 * * 1-5', isActive: true }}>
        {editing ? (
          // The API only edits time and on/off; who and what are fixed once created.
          <div style={{ marginBottom: 16, padding: 12, background: CARD, borderRadius: 8, border: `1px solid ${BORDER}` }}>
            <div style={{ fontWeight: 600, color: '#EAECEF' }}>{props.scheduleLabel(editing)}</div>
            <div style={{ color: MUTED, fontSize: 12.5, marginTop: 2 }}>{props.strategyName(editing.strategyId)}</div>
          </div>
        ) : (
          <>
            <Form.Item name="portfolioId" label="Portfolio" rules={[{ required: true, message: 'Choose a portfolio' }]}>
              <Select placeholder="Select portfolio" options={props.portfolioOptions} showSearch optionFilterProp="label" style={field} />
            </Form.Item>
            <Form.Item name="strategyId" label="Strategy" rules={[{ required: true, message: 'Choose a strategy' }]}>
              <Select placeholder="Select strategy" options={props.strategyOptions} showSearch optionFilterProp="label" style={field} />
            </Form.Item>
          </>
        )}
        <Form.Item
          name="cronDaily"
          label="Schedule (cron)"
          rules={[{ required: true, whitespace: true, message: 'Enter a cron expression' }]}
          extra={<span style={{ color: human ? '#0ECB81' : MUTED }}>{human ?? 'minute hour * * weekdays — e.g. 0 9 * * 1-5'}</span>}
        >
          <Input placeholder="0 9 * * 1-5" inputMode="text" autoCapitalize="off" style={{ ...field, fontFamily: 'monospace' }} />
        </Form.Item>
        <Form.Item name="isActive" valuePropName="checked" style={{ marginBottom: 20 }}>
          <Switch checkedChildren="Active" unCheckedChildren="Paused" />
        </Form.Item>
        <Button type="primary" htmlType="submit" block loading={saving} style={{ height: TOUCH_TARGET, fontWeight: 600 }}>
          {editing ? 'Save' : 'Add daily run'}
        </Button>
        {editing && (
          <Popconfirm
            title="Remove this daily run?"
            onConfirm={async () => {
              if (await props.onDeleteSchedule(editing.id)) onClose();
            }}
            okText="Remove"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button danger type="text" block style={{ height: TOUCH_TARGET, marginTop: 8 }}>
              Remove daily run
            </Button>
          </Popconfirm>
        )}
      </Form>
    </Drawer>
  );
};

const SchedulesList: React.FC<{ props: MobileExecutionsProps; onEdit: (s: DailySchedule) => void; onAdd: () => void }> = ({ props, onEdit, onAdd }) => {
  const [toggling, setToggling] = useState<string | null>(null);
  const { schedules, loadingSchedules } = props;

  return (
    <div>
      <div style={{ color: MUTED, fontSize: 12.5, margin: '0 4px 12px' }}>
        Start a strategy automatically at a set time on trading days.
      </div>
      {loadingSchedules ? (
        <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
      ) : schedules.length === 0 ? (
        <Empty icon={<ClockCircleOutlined />} text="No daily runs yet" />
      ) : (
        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>
          {schedules.map((s, i) => (
            <div
              key={s.id}
              role="button"
              tabIndex={0}
              onClick={() => onEdit(s)}
              onKeyDown={(ev) => { if (ev.key === 'Enter') onEdit(s); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                minHeight: 64,
                padding: '10px 12px 10px 14px',
                borderTop: i ? `1px solid ${BORDER}` : 'none',
                cursor: 'pointer',
                opacity: s.isActive ? 1 : 0.6,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#EAECEF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {props.scheduleLabel(s)}
                </div>
                <div style={{ fontSize: 12.5, color: '#B7BDC6', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {props.strategyName(s.strategyId)}
                </div>
                <div style={{ fontSize: 12, color: '#0ECB81', marginTop: 2 }}>
                  {describeCron(s.cronDaily) ?? <span style={{ fontFamily: 'monospace', color: MUTED }}>{s.cronDaily}</span>}
                </div>
              </div>
              {/* Its own tap target: toggling must not also open the edit sheet. */}
              <span onClick={(ev) => ev.stopPropagation()} style={{ padding: 8, margin: -8 }}>
                <Switch
                  checked={s.isActive}
                  loading={toggling === s.id}
                  aria-label={s.isActive ? 'Pause daily run' : 'Activate daily run'}
                  onChange={async (checked) => {
                    setToggling(s.id);
                    try {
                      await props.onUpdateSchedule(s.id, { isActive: checked });
                    } finally {
                      setToggling(null);
                    }
                  }}
                />
              </span>
            </div>
          ))}
        </div>
      )}
      <Button
        block
        icon={<PlusOutlined />}
        onClick={onAdd}
        style={{ height: TOUCH_TARGET, borderColor: '#0ECB81', color: '#0ECB81', fontWeight: 600 }}
      >
        Add daily run
      </Button>
    </div>
  );
};

/**
 * Executions on a phone: a list a thumb can read, not the desktop table squeezed. Runs are
 * grouped by trading day and open their strategy on tap; daily runs toggle in place and edit
 * in a bottom sheet.
 */
const MobileExecutions: React.FC<MobileExecutionsProps> = (props) => {
  const [section, setSection] = useState<'runs' | 'daily'>('runs');
  const [sheet, setSheet] = useState<SheetState>(null);
  const activeCount = props.executions.filter((e) => executionStatusMeta(e.status).active).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <div style={{ padding: '16px 16px 10px' }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#EAECEF' }}>Executions</h1>
        <div style={{ color: MUTED, fontSize: 12.5, marginTop: 2 }}>
          {activeCount > 0 ? `${activeCount} running now` : 'No bot running'}
        </div>
      </div>
      <SectionPills
        sticky
        activeKey={section}
        onChange={(k) => setSection(k as 'runs' | 'daily')}
        items={[
          { key: 'runs', label: 'Runs' },
          { key: 'daily', label: `Daily runs${props.schedules.length ? ` · ${props.schedules.length}` : ''}` },
        ]}
      />
      {/* Bottom padding clears the floating support button over the last row. */}
      <div style={{ padding: '12px 16px 96px' }}>
        {section === 'runs' ? (
          <>
            <MobileFilterBar
              search={{ value: props.search, placeholder: 'Search executions...', onChange: props.onSearch }}
              groups={props.filterGroups}
              extra={
                <Button
                  icon={<ReloadOutlined />}
                  onClick={props.onRefresh}
                  aria-label="Refresh"
                  style={{ height: TOUCH_TARGET, width: TOUCH_TARGET, flexShrink: 0 }}
                />
              }
            />
            <RunsList
              executions={props.executions}
              loading={props.loading}
              onOpenExecution={props.onOpenExecution}
              strategyName={props.strategyName}
              portfolioLabel={props.portfolioLabel}
            />
          </>
        ) : (
          <SchedulesList props={props} onEdit={(s) => setSheet({ mode: 'edit', schedule: s })} onAdd={() => setSheet({ mode: 'add' })} />
        )}
      </div>
      <ScheduleSheet state={sheet} onClose={() => setSheet(null)} props={props} />
    </div>
  );
};

export default MobileExecutions;
